/**
 * @module ui.marquee
 * @version 4.9.2
 * @description LIFTR Marquee Web Component - CSS-animated infinite scroll marquee
 * with progressive hover, tab visibility pause, and keyboard accessibility.
 * 
 * @element liftr-marquee
 * 
 * @fires liftr:marquee:paused - When animation pauses (hover, visibility, keyboard)
 * @fires liftr:marquee:resumed - When animation resumes
 * 
 * @attr {string} liftr-id - Optional identifier
 * @attr {string} liftr-axis - Scroll direction: x|y (required, set in Liquid)
 * @attr {string} liftr-state - State output: active, paused, disabled
 * 
 * @changelog
 * - 4.9.2: REVERSE DIRECTION FIX
 *        - FIX: Reverse marquee now uses animation-direction: reverse on panel + clone
 *        - FIX: --marquee-direction CSS value changed from number (1/-1) to string (forward/reverse)
 *        - ADD: #applyDirection() sets animation-direction based on CSS_SCHEMA direction
 *        - REMOVE: Keyframe calc() with --marquee-direction (now fixed -100%)
 * - 4.9.1: CONFIG BOUNDARY
 *        - REMOVE: Overlap keys from DEFAULTS (enabled, speed, direction)
 *        - FIX: #updateDuration bridge fallback removed (CSS_SCHEMA is SSOT)
 *        - RULE: DEFAULTS = static JS-only config, CSS_SCHEMA = responsive CSS config
 * - 4.9.0: AUDIT COMPLIANCE - Quality Checks v4.9
 *        - Removed redundant observeResize (watchSettings resize:true covers it)
 *        - Removed unused import (on from core.utilities.js)
 *        - Removed dead config (hoverSlowDuration)
 * - 4.5.0: AUDIT COMPLIANCE - Quality Checks v4.5
 *        - Added ROLES, STATES, EVENTS, SETTINGS, PROPERTIES constants
 *        - CSS_SCHEMA frozen (not exported)
 *        - All string literals → constants
 *        - Correct export pattern
 * - 4.0.0: Initial v4.0 implementation
 * 
 * @example
 * <liftr-marquee liftr-axis="x">
 *   <div liftr-role="marquee-viewport">
 *     <div liftr-role="marquee-panel">Content...</div>
 *     <div liftr-role="marquee-clone" aria-hidden="true">Content...</div>
 *   </div>
 * </liftr-marquee>
 * 
 * @cssvar --marquee-enabled - true|false (CSS Settings)
 * @cssvar --marquee-speed - px/s (CSS Settings)
 * @cssvar --marquee-direction - forward|reverse (CSS Settings)
 * @cssvar --marquee-duration - Calculated animation duration (Output)
 */

import { LiftrModule } from './core.module.js';
import { ATTRIBUTES } from './core.liftr.js';
import { prefersReducedMotion } from './core.utilities.js';

/* ═══════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Role constants for DOM queries.
 * @type {Readonly<Record<string, string>>}
 */
const ROLES = Object.freeze({
  VIEWPORT: 'marquee-viewport',
  PANEL:    'marquee-panel',
  CLONE:    'marquee-clone'
});

/**
 * State tokens for liftr-state attribute.
 * @type {Readonly<Record<string, string>>}
 */
const STATES = Object.freeze({
  ACTIVE:   'active',
  PAUSED:   'paused',
  DISABLED: 'disabled'
});

/**
 * Event action names (auto-prefixed with liftr:marquee:).
 * @type {Readonly<Record<string, string>>}
 */
const EVENTS = Object.freeze({
  PAUSED:  'paused',
  RESUMED: 'resumed'
});

/**
 * Settings tokens for liftr-settings attribute.
 * @type {Readonly<Record<string, string>>}
 */
const SETTINGS = Object.freeze({
  // Reserved for future use
});

/**
 * CSS Custom Properties set by JavaScript.
 * @type {Readonly<Record<string, string>>}
 */
const PROPERTIES = Object.freeze({
  DURATION: '--marquee-duration'
});

/* ═══════════════════════════════════════════════════════════════════
 * CSS SETTINGS SCHEMA (internal, not exported)
 * ═══════════════════════════════════════════════════════════════════ */

const CSS_SCHEMA = Object.freeze({
  speed: { type: 'number', default: 50 },
  direction: { type: 'string', default: 'forward', valid: ['forward', 'reverse'] }
});

/* ═══════════════════════════════════════════════════════════════════
 * LIFTR MARQUEE WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════ */

class LiftrMarquee extends LiftrModule {
  
  /* ═══════════════════════════════════════════════════════════════════
   * STATIC PROPERTIES
   * ═══════════════════════════════════════════════════════════════════ */
  
  static NAME = 'marquee';
  
  static DEFAULTS = {
    pauseOnHover: true,
    hoverSlowRate: 0.2,
    hoverPauseDelay: 500,
    pauseOnHidden: true,
    keyboardToggle: true
  };
  
  /* ═══════════════════════════════════════════════════════════════════
   * INSTANCE PROPERTIES
   * ═══════════════════════════════════════════════════════════════════ */
  
  /** @type {HTMLElement|null} */
  #viewport = null;
  
  /** @type {HTMLElement|null} */
  #panel = null;
  
  /** @type {HTMLElement|null} */
  #clone = null;
  
  /** @type {string} */
  #axis = 'x';
  
  /** @type {Object|null} */
  #watcher = null;
  
  /** @type {Object|null} */
  #cssSettings = null;
  
  /** @type {number|null} */
  #hoverTimeout = null;
  
  /** @type {boolean} */
  #isPausedByUser = false;
  
  /** @type {boolean} */
  #isPausedByVisibility = false;
  
  /* ═══════════════════════════════════════════════════════════════════
   * LIFECYCLE
   * ═══════════════════════════════════════════════════════════════════ */
  
  mount() {
    // Query required elements using ROLES constants
    this.#viewport = this.role(ROLES.VIEWPORT);
    this.#panel = this.role(ROLES.PANEL);
    this.#clone = this.role(ROLES.CLONE);
    
    // Validate required elements
    if (!this.#viewport || !this.#panel || !this.#clone) {
      throw new Error('Marquee requires viewport, panel, and clone roles');
    }
    
    // Read axis input attribute
    this.#axis = this.getAttribute(ATTRIBUTES.AXIS) || 'x';
    
    // Initialize CSS Settings watcher
    this.#watcher = this.watchSettings('marquee', {
      enabled: '--marquee-enabled',
      css: CSS_SCHEMA,
      resize: true,
      debounce: 100
    }, {
      onEnable: (settings) => this.#onEnable(settings),
      onDisable: () => this.#onDisable(),
      onChange: (changes) => this.#onChange(changes)
    });
    
    // Check initial enabled state
    if (this.#watcher.isEnabled()) {
      this.#cssSettings = this.#watcher.getCSS();
      this.#enableMarquee();
    }
  }
  
  unmount() {
    this.#disableMarquee();
    super.unmount();
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * WATCHER CALLBACKS
   * ═══════════════════════════════════════════════════════════════════ */
  
  #onEnable(settings) {
    this.#cssSettings = settings.css;
    this.#enableMarquee();
  }
  
  #onDisable() {
    this.#disableMarquee();
  }
  
  #onChange(changes) {
    if (changes.css) {
      this.#cssSettings = changes.css;
      this.#updateDuration();
      this.#applyDirection();
    }
    if (changes.size) {
      this.#updateDuration();
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * ENABLE / DISABLE
   * ═══════════════════════════════════════════════════════════════════ */
  
  #enableMarquee() {
    // Check reduced motion preference
    if (prefersReducedMotion()) {
      this.state.add(STATES.DISABLED);
      return;
    }
    
    // Calculate and set duration
    this.#updateDuration();
    
    // Apply direction (forward or reverse)
    this.#applyDirection();
    
    // Setup behaviors
    this.#setupHoverBehavior();
    this.#setupVisibilityBehavior();
    this.#setupKeyboardBehavior();
    
    // Activate using STATES constant
    this.state.add(STATES.ACTIVE);
    this.state.remove(STATES.DISABLED);
  }
  
  #disableMarquee() {
    // Clear hover timeout
    if (this.#hoverTimeout) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }
    
    // Reset state using STATES constants
    this.state.remove(STATES.ACTIVE);
    this.state.remove(STATES.PAUSED);
    this.state.add(STATES.DISABLED);
    
    // Clear duration using PROPERTIES constant
    this.style.removeProperty(PROPERTIES.DURATION);
    
    // Clear animation-direction
    this.#panel?.style.removeProperty('animation-direction');
    this.#clone?.style.removeProperty('animation-direction');
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * DURATION CALCULATION
   * ═══════════════════════════════════════════════════════════════════ */
  
  #updateDuration() {
    if (!this.#panel || !this.#cssSettings) return;
    
    // Get content dimension based on axis
    const contentSize = this.#axis === 'y'
      ? this.#panel.scrollHeight
      : this.#panel.scrollWidth;
    
    // Calculate duration: size / speed (CSS_SCHEMA is SSOT, default: 50)
    const speed = this.#cssSettings?.speed ?? CSS_SCHEMA.speed.default;
    const duration = contentSize / speed;
    
    // Set CSS custom property using PROPERTIES constant
    this.style.setProperty(PROPERTIES.DURATION, `${duration}s`);
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * DIRECTION
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Apply animation-direction based on CSS Settings direction value.
   * Forward: normal playback (translateX(0) -> translateX(-100%))
   * Reverse: reversed playback (translateX(-100%) -> translateX(0))
   */
  #applyDirection() {
    const dir = this.#cssSettings?.direction ?? CSS_SCHEMA.direction.default;
    const animDir = dir === 'reverse' ? 'reverse' : 'normal';
    this.#panel.style.animationDirection = animDir;
    this.#clone.style.animationDirection = animDir;
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * ANIMATION HELPERS
   * ═══════════════════════════════════════════════════════════════════ */
  
  #getAllAnimations() {
    const anims = [];
    if (this.#panel) anims.push(...this.#panel.getAnimations());
    if (this.#clone) anims.push(...this.#clone.getAnimations());
    return anims;
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * HOVER BEHAVIOR
   * ═══════════════════════════════════════════════════════════════════ */
  
  #setupHoverBehavior() {
    if (!this.config.pauseOnHover) return;
    
    const handleMouseEnter = () => {
      if (this.#isPausedByUser || this.#isPausedByVisibility) return;
      
      const anims = this.#getAllAnimations();
      if (!anims.length) return;
      
      // Phase 1: Slow down
      anims.forEach(a => {
        a.updatePlaybackRate(this.config.hoverSlowRate);
      });
      
      // Phase 2: Pause after delay
      this.#hoverTimeout = setTimeout(() => {
        anims.forEach(a => a.pause());
        this.state.add(STATES.PAUSED);
        this.emit(EVENTS.PAUSED, { trigger: 'hover' });
      }, this.config.hoverPauseDelay);
    };
    
    const handleMouseLeave = () => {
      // Clear pending pause
      if (this.#hoverTimeout) {
        clearTimeout(this.#hoverTimeout);
        this.#hoverTimeout = null;
      }
      
      if (this.#isPausedByUser || this.#isPausedByVisibility) return;
      
      const anims = this.#getAllAnimations();
      if (!anims.length) return;
      
      // Resume at normal speed
      anims.forEach(a => {
        a.updatePlaybackRate(1);
        a.play();
      });
      
      this.state.remove(STATES.PAUSED);
      this.emit(EVENTS.RESUMED, { trigger: 'hover' });
    };
    
    this.addEventListener('mouseenter', handleMouseEnter);
    this.addEventListener('mouseleave', handleMouseLeave);
    
    this.addDisposable(() => {
      this.removeEventListener('mouseenter', handleMouseEnter);
      this.removeEventListener('mouseleave', handleMouseLeave);
      if (this.#hoverTimeout) {
        clearTimeout(this.#hoverTimeout);
        this.#hoverTimeout = null;
      }
    });
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * VISIBILITY BEHAVIOR
   * ═══════════════════════════════════════════════════════════════════ */
  
  #setupVisibilityBehavior() {
    if (!this.config.pauseOnHidden) return;
    
    const handleVisibilityChange = () => {
      const anims = this.#getAllAnimations();
      if (!anims.length) return;
      
      if (document.hidden) {
        this.#isPausedByVisibility = true;
        anims.forEach(a => a.pause());
        this.state.add(STATES.PAUSED);
        this.emit(EVENTS.PAUSED, { trigger: 'visibility' });
      } else {
        this.#isPausedByVisibility = false;
        if (!this.#isPausedByUser) {
          anims.forEach(a => a.play());
          this.state.remove(STATES.PAUSED);
          this.emit(EVENTS.RESUMED, { trigger: 'visibility' });
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Register cleanup via addDisposable
    this.addDisposable(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    });
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * KEYBOARD BEHAVIOR
   * ═══════════════════════════════════════════════════════════════════ */
  
  #setupKeyboardBehavior() {
    if (!this.config.keyboardToggle) return;
    
    // Ensure focusable
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
    
    const handleKeydown = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        
        const anims = this.#getAllAnimations();
        if (!anims.length) return;
        
        this.#isPausedByUser = !this.#isPausedByUser;
        
        if (this.#isPausedByUser) {
          anims.forEach(a => a.pause());
          this.state.add(STATES.PAUSED);
          this.emit(EVENTS.PAUSED, { trigger: 'keyboard' });
        } else {
          anims.forEach(a => a.play());
          this.state.remove(STATES.PAUSED);
          this.emit(EVENTS.RESUMED, { trigger: 'keyboard' });
        }
      }
    };
    
    this.addEventListener('keydown', handleKeydown);
    this.addDisposable(() => this.removeEventListener('keydown', handleKeydown));
  }
}

/* ═══════════════════════════════════════════════════════════════════
 * REGISTER WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════ */

customElements.define('liftr-marquee', LiftrMarquee);

/* ═══════════════════════════════════════════════════════════════════
 * EXPORTS
 * ═══════════════════════════════════════════════════════════════════ */

export { LiftrMarquee, ROLES, STATES, EVENTS, SETTINGS, PROPERTIES };